import React, { useState, useRef, useEffect } from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

function FileItem(props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [renameMode, setRenameMode] = useState(false);
  const [newFileName, setNewFileName] = useState(props.file.name);
  const [isOpening, setIsOpening] = useState(false);

  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when entering rename mode
  useEffect(() => {
    if (renameMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [renameMode]);

  const handleContainerClick = async (e) => {
    // Only trigger file click if we're not in rename mode and didn't click menu items
    if (!renameMode && !isMenuOpen && !isOpening) {
      setIsOpening(true);
      await props.handleFileClick(props.file.uuid);
      setIsOpening(false);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    props.delete_file(props.file.uuid);
    setIsMenuOpen(false);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setRenameMode(true);
    setIsMenuOpen(false);
  };

  const handleRenameConfirm = (e) => {
    e.stopPropagation();
    if (newFileName.trim()) {
      props.rename_file(props.file.uuid, newFileName);
      setRenameMode(false);
    }
  };

  const handleInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRenameConfirm(e);
    } else if (e.key === 'Escape') {
      setRenameMode(false);
      setNewFileName(props.file.name);
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const fileType = props.file?.extension;
  const isImage = ['jpg', 'jpeg', 'png', 'svg'].includes(fileType);
  const isPdf = fileType === 'pdf';
  const [previewError, setPreviewError] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    // Date-ONLY strings ("2026-06-12") parse as UTC midnight, which
    // toLocaleDateString renders as the PREVIOUS day in any timezone
    // behind UTC — files created this morning showed yesterday's date.
    // Parse them as a local date instead; full ISO datetimes carry
    // their own offset and localize correctly as-is.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // sizeClass lets callers size the icon per view (list keeps the original
  // w-6 h-6; grid bumps to w-8 h-8). Both literals appear verbatim below / at the
  // call site so Tailwind's JIT picks them up.
  const FileIcon = ({ type, sizeClass = "w-6 h-6" }) => {
    switch (type) {
      case 'folder':
        return <FolderIcon className={`${sizeClass} text-violet-700`} />;
      case 'jpeg':
      case 'jpg' :
      case 'png':
      case 'svg':
        return <ImageIcon className={`${sizeClass} text-orange-500`} />;
      case 'pdf':
        return <PictureAsPdfIcon className={`${sizeClass} text-red-500`} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon className={`${sizeClass} text-green-500`} />;
      case 'doc':
      case 'docx':
        return <DescriptionIcon className={`${sizeClass} text-blue-500`} />;
      case 'txt':
        return <TextSnippetIcon className={`${sizeClass} text-gray-500`} />;
      default:
        return <DescriptionIcon className={`${sizeClass} text-gray-500`} />;
    }
  };

  // Finder-style: show a small thumbnail in place of the generic icon for image
  // files. Reuses the eager presigned_url and the same previewError fallback the
  // gallery preview uses (falls back to the icon on a load error). Lazy so
  // off-screen rows don't fetch the full image.
  const FileThumbOrIcon = ({ sizeClass = "w-6 h-6" }) =>
    isImage && !previewError && props.file?.presigned_url ? (
      <img
        src={props.file.presigned_url}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setPreviewError(true)}
        className={`${sizeClass} rounded object-cover flex-shrink-0 bg-slate-100`}
      />
    ) : (
      <FileIcon type={fileType} sizeClass={sizeClass} />
    );

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(props.file));
    props.setIsFileDragging(true)
  };

  const handleDragEnd = (e) => {
    props.setIsFileDragging(false)
  };

  const menuDropdown = isMenuOpen && (
    <div
      ref={menuRef}
      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 py-1"
      style={{ top: '100%' }}
    >
      <button
        onClick={async (e) => {
          e.stopPropagation();
          setIsMenuOpen(false);
          setIsOpening(true);
          await props.handleFileClick(props.file.uuid);
          setIsOpening(false);
        }}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Open
      </button>
      <button
        onClick={handleRenameClick}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Rename
      </button>
      {props.onViewPhotos && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(false);
            props.onViewPhotos(props.file);
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          View extracted photos
        </button>
      )}
      <button
        onClick={handleDeleteClick}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Delete
      </button>
    </div>
  );

  const LargeFileIcon = ({ type }) => {
    const iconStyle = { fontSize: 48 };
    switch (type) {
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'svg':
        return <ImageIcon style={iconStyle} className="text-orange-300" />;
      case 'pdf':
        return <PictureAsPdfIcon style={iconStyle} className="text-red-300" />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon style={iconStyle} className="text-green-300" />;
      case 'doc':
      case 'docx':
        return <DescriptionIcon style={iconStyle} className="text-blue-300" />;
      case 'txt':
        return <TextSnippetIcon style={iconStyle} className="text-gray-300" />;
      default:
        return <DescriptionIcon style={iconStyle} className="text-gray-300" />;
    }
  };

  if (props.viewMode === 'preview') {
    return (
      <div
        className="border rounded-lg bg-white hover:bg-gray-50 cursor-pointer relative"
        onClick={handleContainerClick}
        draggable={!renameMode && props.canEditDocuments && !props.isProtected}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {isOpening && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-lg z-10">
            <CircularProgress size={20} style={{ color: '#6d28d9' }} />
          </div>
        )}
        <div className={`h-48 bg-gray-50 flex items-center justify-center overflow-hidden rounded-t-lg ${isPdf && !previewError ? 'p-3' : ''}`}>
          {isImage && !previewError ? (
            <img
              src={props.file.presigned_url}
              alt={props.file.name}
              className="w-full h-full object-cover"
              onError={() => setPreviewError(true)}
            />
          ) : isPdf && !previewError ? (
            <iframe
              src={props.file.presigned_url}
              title={props.file.name}
              className="w-full h-full border-0 pointer-events-none rounded"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <LargeFileIcon type={fileType} />
          )}
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <FileIcon type={fileType} />
              {renameMode ? (
                <div className="flex items-center space-x-2 flex-1" onClick={handleInputClick}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
                  />
                  <button
                    onClick={handleRenameConfirm}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <CheckIcon className="w-4 h-4 text-emerald-600" />
                  </button>
                </div>
              ) : (
                <Tooltip title={props.file.displayName || props.file.name}>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {props.file.displayName || props.file.name}
                  </p>
                </Tooltip>
              )}
            </div>
            {!renameMode && props.isProtected && (
              <div className="w-8 h-8 flex items-center justify-center">
                <LockIcon style={{ fontSize: 16, color: '#9CA3AF' }} />
              </div>
            )}
            {!renameMode && props.canEditDocuments && !props.isProtected && (
              <button
                ref={buttonRef}
                onClick={handleMenuClick}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <MoreVertIcon className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-8">
            {props.file.fileSize} • {formatDate(props.file.created_at_date)}
          </p>
        </div>
        {menuDropdown}
      </div>
    );
  }

  if (props.viewMode === 'list') {
    return (
      <div
        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer relative border-b border-gray-100"
        onClick={handleContainerClick}
        draggable={!renameMode && props.canEditDocuments && !props.isProtected}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {isOpening && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-10">
            <CircularProgress size={16} style={{ color: '#6d28d9' }} />
          </div>
        )}
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <FileThumbOrIcon />
          {renameMode ? (
            <div className="flex items-center space-x-2 flex-1" onClick={handleInputClick}>
              <input
                ref={inputRef}
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
              />
              <button
                onClick={handleRenameConfirm}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <CheckIcon className="w-4 h-4 text-emerald-600" />
              </button>
            </div>
          ) : (
            <Tooltip title={props.file.displayName || props.file.name}>
              <p className="text-sm text-gray-900 truncate">
                {props.file.displayName || props.file.name}
              </p>
            </Tooltip>
          )}
        </div>
        <span className="w-36 text-right text-xs text-gray-500 shrink-0">{props.file.fileSize || '—'}</span>
        <span className="w-32 text-right text-xs text-gray-500 shrink-0">{formatDate(props.file.created_at_date)}</span>
        <span className="w-20 text-right text-xs text-gray-400 uppercase shrink-0">{props.file.extension || '—'}</span>
        {!renameMode && props.isProtected ? (
          <span className="w-6 h-6 flex items-center justify-center ml-3 shrink-0">
            <LockIcon style={{ fontSize: 14, color: '#9CA3AF' }} />
          </span>
        ) : !renameMode && props.canEditDocuments && !props.isProtected ? (
          <button
            ref={buttonRef}
            onClick={handleMenuClick}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full ml-3 shrink-0"
          >
            <MoreVertIcon style={{ fontSize: 18 }} className="text-gray-400" />
          </button>
        ) : (
          <span className="w-6 h-6 ml-3 shrink-0"></span>
        )}
        {menuDropdown}
      </div>
    );
  }

  return (
    <div
      className="p-4 border rounded-lg bg-white hover:bg-gray-50 cursor-pointer relative"
      onClick={handleContainerClick}
      draggable={!renameMode && props.canEditDocuments && !props.isProtected}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {isOpening && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-lg z-10">
          <CircularProgress size={20} style={{ color: '#6d28d9' }} />
        </div>
      )}
      <div className="flex items-start space-x-3">
        <FileThumbOrIcon sizeClass="w-8 h-8" />
        <div className="flex-1 min-w-0">
          {renameMode ? (
            <div className="flex items-center space-x-2" onClick={handleInputClick}>
              <input
                ref={inputRef}
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
              />
              <button
                onClick={handleRenameConfirm}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <CheckIcon className="w-5 h-5 text-emerald-600" />
              </button>
            </div>
          ) : (
            <Tooltip title={props.file.displayName || props.file.name}>
              <p className="text-sm font-medium text-gray-900 truncate">
                {props.file.displayName || props.file.name}
              </p>
            </Tooltip>
          )}
          <p className="text-xs text-gray-500">
            {props.file.fileSize} • {formatDate(props.file.created_at_date)}
          </p>
        </div>
        {!renameMode && props.isProtected && (
          <div className="w-8 h-8 flex items-center justify-center">
            <LockIcon style={{ fontSize: 16, color: '#9CA3AF' }} />
          </div>
        )}
        {!renameMode && props.canEditDocuments && !props.isProtected && (
          <button
            ref={buttonRef}
            onClick={handleMenuClick}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <MoreVertIcon className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>
      {menuDropdown}
    </div>
  );
}

export default FileItem;
